/** The My Bookings list (`TripsScreen`, `layout_list_toolbar_collapsible.xml`) with this
 *  tour's card — where the app shows a tour's cover image, name, description and dates.
 *
 *  The header is drawn expanded: the status bar, then the HeaderView's title in
 *  `TitleStyle.LARGE` (Title1). Its search box and "+" button, and every other booking in
 *  the list, are not drawn (`mapMyTrips` notes). A trip the tour hides from My Bookings
 *  has no card at all — the phone says so instead of drawing an empty list that looks
 *  like a bug.
 *
 *  Spec §1E. */
import { adaptMyTrips } from './adapters/myTrips';
import { MyTripsItemView, type MyTripsItemContext } from './items/MyTripsItems';
import { mapMyTrips } from './mappers/myTrips';
import { imageWidthPx } from '../../deviceProfiles';
import type { ScreenProps } from '../../types';

export function MyTripsScreen({ payload, device, imagesBase, meta, now }: ScreenProps & { /** Tests pin it. */ now?: Date }) {
  const ui = mapMyTrips(adaptMyTrips(payload, meta), now);
  const ctx: MyTripsItemContext = { imagesBase, imageWidthPx: imageWidthPx(device) };
  return (
    <div data-and-screen="my-trips" style={{ position: 'relative', height: '100%', overflow: 'hidden', background: 'var(--and-color-white)' }}>
      {/* AppBarToolbarView, expanded: status bar + HeaderView (title LARGE). */}
      <div style={{ height: device.statusBarDp }} />
      <div style={{ minHeight: 56, display: 'flex', alignItems: 'center', padding: '0 16px' }}>
        <div className="and-type-title1" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ui.title}
        </div>
      </div>
      {/* RecyclerView, paddingBottom 16dp. A column: MarginItem offsets never collapse. */}
      <div
        style={{
          height: `calc(100% - ${device.statusBarDp + 56}px)`,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 16 + device.navBarDp,
        }}
      >
        {ui.hidden ? (
          <div className="and-type-caption-dark-medium" data-and-hidden style={{ margin: '48px 32px', textAlign: 'center' }}>
            Not in My Bookings: this tour’s “Show bookings in My Trips” is off, so the app does not get the trip.
          </div>
        ) : (
          ui.items.map((item) => <MyTripsItemView key={item.key} item={item} ctx={ctx} />)
        )}
      </div>
    </div>
  );
}

export function myTripsScreenNotes(payload: Record<string, unknown>, meta?: Record<string, unknown>): string[] {
  return mapMyTrips(adaptMyTrips(payload, meta)).notes;
}
